import { Box, Center, Container, Group, Loader, Menu, Stack, Text, UnstyledButton } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Link, Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom';
import { PushOnboarding } from './components/PushOnboarding.js';
import { reportDeviceState } from './lib/push.js';
import {
  ApiError,
  getMe,
  getSettings,
  listObjects,
  logout,
  switchAccount,
  type AuthUser,
  type RealUser,
} from './lib/api.js';
import { Login } from './pages/Login.js';
import { Account } from './pages/Account.js';
import { AdminDecisions } from './pages/AdminDecisions.js';
import { AdminOps } from './pages/AdminOps.js';
import { AdminUsers } from './pages/AdminUsers.js';
import { Dashboard } from './pages/Dashboard.js';
import { FunnelTuning } from './pages/FunnelTuning.js';
import { LLMStatus } from './pages/LLMStatus.js';
import { Matches } from './pages/Matches.js';
import { Merkliste } from './pages/Merkliste.js';
import { SearchProfiles } from './pages/SearchProfiles.js';
import { Settings } from './pages/Settings.js';
import { ObjectDetail } from './pages/ObjectDetail.js';
import { ObjectList } from './pages/ObjectList.js';
import { Contacted } from './pages/Contacted.js';
import { PartnerCheck } from './pages/PartnerCheck.js';
import { Swipe } from './pages/Swipe.js';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  badge?: number;
}

function useNavBadges(): { partner: number; match: number } {
  // Phase 4: Objekte die auf die Bewertung des Partners warten.
  const { data: partner } = useQuery({
    queryKey: ['nav-badge-partner'],
    queryFn: () => listObjects({ status: 'partner_review', minScore: 0, limit: 200 }),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  // Matches: beide Hot, Kontakt noch nicht aufgenommen.
  const { data: match } = useQuery({
    queryKey: ['nav-badge-match'],
    queryFn: () => listObjects({ status: 'match', minScore: 0, limit: 200 }),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  return { partner: partner?.totalCount ?? 0, match: match?.totalCount ?? 0 };
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Swipe', icon: '🔥' },
  { to: '/merkliste', label: 'Merkliste', icon: '❤️' },
  { to: '/partner', label: 'Partner', icon: '🤝' },
  { to: '/matches', label: 'Matches', icon: '🎯' },
  { to: '/list', label: 'Liste', icon: '📋' },
  { to: '/health', label: 'Übersicht', icon: '📊' },
];

function isActive(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname.startsWith(to);
}

function TopNavLink({ item, badge }: { item: NavItem; badge?: number }): JSX.Element {
  const loc = useLocation();
  const active = isActive(loc.pathname, item.to);
  return (
    <Link
      to={item.to}
      style={{
        position: 'relative',
        color: active ? '#0f172a' : '#64748b',
        fontWeight: active ? 600 : 500,
        textDecoration: 'none',
        fontSize: '0.875rem',
        padding: '6px 10px',
        borderRadius: 8,
        background: active ? 'rgba(124,58,237,0.08)' : 'transparent',
        transition: 'background 100ms',
      }}
    >
      {item.label}
      {badge !== undefined && badge > 0 && (
        <Text
          component="span"
          ml={6}
          c="white"
          fw={700}
          style={{
            fontSize: '0.7rem',
            background: '#f59e0b',
            padding: '1px 6px',
            borderRadius: 999,
            verticalAlign: 'middle',
          }}
        >
          {badge}
        </Text>
      )}
    </Link>
  );
}

function BottomNavLink({ item, badge }: { item: NavItem; badge?: number }): JSX.Element {
  const loc = useLocation();
  const active = isActive(loc.pathname, item.to);
  return (
    <Link
      to={item.to}
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        textDecoration: 'none',
        color: active ? '#7c3aed' : '#64748b',
        fontWeight: active ? 700 : 500,
        padding: '6px 0 8px',
        position: 'relative',
        transition: 'color 150ms',
      }}
      aria-current={active ? 'page' : undefined}
    >
      <span style={{ fontSize: '1.25rem', lineHeight: 1 }}>{item.icon}</span>
      <span style={{ fontSize: '0.7rem', letterSpacing: '0.01em' }}>{item.label}</span>
      {badge !== undefined && badge > 0 && (
        <span
          style={{
            position: 'absolute',
            top: 2,
            right: 'calc(50% - 22px)',
            background: '#f59e0b',
            color: '#fff',
            fontSize: '0.65rem',
            fontWeight: 700,
            padding: '0 5px',
            borderRadius: 999,
            lineHeight: '14px',
            minWidth: 14,
            textAlign: 'center',
          }}
        >
          {badge}
        </span>
      )}
      {active && (
        <span
          style={{
            position: 'absolute',
            bottom: 0,
            left: '30%',
            right: '30%',
            height: 2,
            background: '#7c3aed',
            borderRadius: 2,
          }}
        />
      )}
    </Link>
  );
}

function Brand(): JSX.Element {
  return (
    <Link
      to="/"
      style={{
        textDecoration: 'none',
        color: 'inherit',
        display: 'flex',
        alignItems: 'baseline',
        gap: 8,
      }}
    >
      <Text
        component="span"
        fw={800}
        size="xl"
        style={{
          letterSpacing: '-0.04em',
          background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}
      >
        betongold
      </Text>
      <Text component="span" size="xs" c="dimmed" fw={500}>
        Immobilien-Radar
      </Text>
    </Link>
  );
}

function UserMenu({
  user,
  realUser,
  impersonating,
  googleConfigured,
}: {
  user: AuthUser;
  realUser: RealUser | null;
  impersonating: boolean;
  googleConfigured: boolean;
}): JSX.Element {
  const qc = useQueryClient();
  const initial = (user.name ?? user.email).charAt(0).toUpperCase();
  // Owner-only-UI hängt an der ECHTEN Identität, nicht am angesehenen Konto —
  // sonst verschwindet sie während einer Impersonation.
  const ownerControls = realUser?.isOwner ?? user.isOwner;

  const handleLogout = async (): Promise<void> => {
    await logout();
    qc.clear();
    window.location.href = '/';
  };

  const handleBackToOwner = async (): Promise<void> => {
    if (!realUser) return;
    await switchAccount(realUser.id);
    qc.clear();
    window.location.href = '/';
  };

  return (
    <Menu shadow="md" width={230} position="bottom-end">
      <Menu.Target>
        <UnstyledButton
          aria-label="Benutzermenü"
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
            color: '#fff',
            fontWeight: 700,
            fontSize: '0.85rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {initial}
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label style={{ wordBreak: 'break-all' }}>
          {impersonating && (
            <Text size="xs" c="orange.7" fw={700} mb={2}>
              👁 Angesehen als
            </Text>
          )}
          {user.name ?? user.email}
          {user.name && (
            <Text size="xs" c="dimmed" style={{ wordBreak: 'break-all' }}>
              {user.email}
            </Text>
          )}
        </Menu.Label>
        {impersonating && realUser && (
          <>
            <Menu.Item color="orange" onClick={handleBackToOwner}>
              ↩ Zurück zu {realUser.name ?? 'meinem Konto'}
            </Menu.Item>
            <Menu.Divider />
          </>
        )}
        <Menu.Item component={Link} to="/settings">
          ⚙️ Einstellungen
        </Menu.Item>
        <Menu.Item component={Link} to="/account">
          👤 {impersonating ? 'Konto' : 'Mein Konto'}
        </Menu.Item>
        {ownerControls && (
          <Menu.Item component={Link} to="/admin/users">
            👥 Nutzerverwaltung
          </Menu.Item>
        )}
        {ownerControls && (
          <Menu.Item component={Link} to="/admin/ops">
            🩺 Betrieb & Diagnose
          </Menu.Item>
        )}
        {ownerControls && (
          <Menu.Item component={Link} to="/admin/decisions">
            🗳 Entscheidungen
          </Menu.Item>
        )}
        <Menu.Divider />
        {!user.viaSession && googleConfigured && (
          <Menu.Item component="a" href="/api/auth/google">
            🔑 Mit Google anmelden
          </Menu.Item>
        )}
        {!user.viaSession && !googleConfigured && (
          <Menu.Item disabled>Login noch nicht konfiguriert</Menu.Item>
        )}
        {user.viaSession && (
          <Menu.Item color="red" onClick={handleLogout}>
            Abmelden
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}

function OnboardingBanner({ user }: { user: AuthUser }): JSX.Element | null {
  const loc = useLocation();
  const { data } = useQuery({ queryKey: ['settings'], queryFn: getSettings });
  // Übergangsmodus (kein echter Login) + Settings-Seite selbst → kein Banner.
  if (!user.viaSession) return null;
  if (loc.pathname.startsWith('/settings')) return null;
  if (!data) return null;
  const hasPrimary = data.anchors.some((a) => a.role === 'primary' && a.lat !== null);
  if (hasPrimary) return null;

  return (
    <Box
      style={{
        background: 'linear-gradient(135deg, rgba(124,58,237,0.10), rgba(37,99,235,0.06))',
        border: '1px solid rgba(124,58,237,0.25)',
        borderRadius: 12,
        padding: '14px 16px',
        marginBottom: 16,
      }}
    >
      <Group justify="space-between" wrap="wrap" gap="sm">
        <Stack gap={2} style={{ minWidth: 0 }}>
          <Text fw={700}>👋 Willkommen bei betongold!</Text>
          <Text size="sm" c="dimmed">
            Richte zuerst deinen Haupt-Pendelort ein (z. B. Arbeit) und verbinde dein Postfach —
            dann startet dein persönlicher Radar.
          </Text>
        </Stack>
        <Link to="/settings" style={{ textDecoration: 'none' }}>
          <Box
            component="span"
            style={{
              display: 'inline-block',
              background: '#7c3aed',
              color: '#fff',
              padding: '8px 16px',
              borderRadius: 8,
              fontWeight: 600,
              fontSize: '0.875rem',
            }}
          >
            Einrichten →
          </Box>
        </Link>
      </Group>
    </Box>
  );
}

function ImpersonationBanner({
  user,
  realUser,
  viewAsLogin,
}: {
  user: AuthUser;
  realUser: RealUser | null;
  viewAsLogin: string | null;
}): JSX.Element {
  const qc = useQueryClient();
  const back = async (): Promise<void> => {
    if (!realUser) return;
    await switchAccount(realUser.id);
    qc.clear();
    window.location.href = '/';
  };
  return (
    <Box
      style={{
        background: '#f59f00',
        color: '#1a1300',
        position: 'sticky',
        top: 0,
        zIndex: 20,
      }}
    >
      <Container size="md" py={6} px="sm">
        <Group justify="space-between" align="center" wrap="nowrap" gap="xs">
          <Text size="sm" fw={600} style={{ minWidth: 0 }}>
            👁 Du siehst das Konto <b>{user.name ?? user.email}</b>
            {viewAsLogin ? (
              <>
                {' '}
                als <b>{viewAsLogin.split('@')[0]}</b>
              </>
            ) : null}
          </Text>
          <UnstyledButton
            onClick={back}
            style={{
              background: 'rgba(0,0,0,0.15)',
              borderRadius: 6,
              padding: '4px 10px',
              fontSize: '0.8rem',
              fontWeight: 700,
              whiteSpace: 'nowrap',
            }}
          >
            ↩ Zurück
          </UnstyledButton>
        </Group>
      </Container>
    </Box>
  );
}

function AppShell({
  user,
  realUser,
  impersonating,
  viewAsLogin,
  googleConfigured,
}: {
  user: AuthUser;
  realUser: RealUser | null;
  impersonating: boolean;
  viewAsLogin: string | null;
  googleConfigured: boolean;
}): JSX.Element {
  const badges = useNavBadges();

  // Geräte-/Push-Zustand an den Server melden (Installations-Fortschritt fürs Admin-
  // Tracking). Nur echte Session, nicht während „Ansehen als". Zusätzlich beim Zurück-
  // Wechseln in den Tab neu melden — so wird ein in derselben Sitzung aktivierter Push
  // (oder eine frische PWA-Installation) sichtbar, ohne Reload.
  useEffect(() => {
    if (!(user.viaSession && !impersonating)) return;
    void reportDeviceState();
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void reportDeviceState();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user.viaSession, impersonating]);

  return (
    <Box style={{ minHeight: '100vh', background: '#fafafa' }}>
      {impersonating && (
        <ImpersonationBanner user={user} realUser={realUser} viewAsLogin={viewAsLogin} />
      )}
      {/* Desktop / Tablet Top-Nav */}
      <Box
        component="header"
        visibleFrom="sm"
        style={{
          background: '#ffffff',
          borderBottom: '1px solid rgba(15,23,42,0.06)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backdropFilter: 'saturate(180%) blur(8px)',
        }}
      >
        <Container size="md" py="sm">
          <Group justify="space-between" align="center" wrap="nowrap" gap="xs">
            <Brand />
            <Group gap="sm" wrap="nowrap">
              <Group gap={4} wrap="nowrap">
                {NAV_ITEMS.map((item) => (
                  <TopNavLink
                    key={item.to}
                    item={item}
                    badge={
                      item.to === '/partner'
                        ? badges.partner
                        : item.to === '/matches'
                          ? badges.match
                          : undefined
                    }
                  />
                ))}
              </Group>
              <UserMenu
                user={user}
                realUser={realUser}
                impersonating={impersonating}
                googleConfigured={googleConfigured}
              />
            </Group>
          </Group>
        </Container>
      </Box>

      {/* Mobile Top-Header (kompakt, nur Brand) */}
      <Box
        component="header"
        hiddenFrom="sm"
        style={{
          background: '#ffffff',
          borderBottom: '1px solid rgba(15,23,42,0.06)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backdropFilter: 'saturate(180%) blur(8px)',
        }}
      >
        <Container size="md" py="xs" px="sm">
          <Group justify="space-between" align="center" wrap="nowrap">
            {/* Spacer in Menü-Breite, damit die Brand optisch zentriert bleibt */}
            <Box style={{ width: 32 }} />
            <Brand />
            <UserMenu
              user={user}
              realUser={realUser}
              impersonating={impersonating}
              googleConfigured={googleConfigured}
            />
          </Group>
        </Container>
      </Box>

      <Container
        size="md"
        py={{ base: 'md', sm: 'xl' }}
        px={{ base: 'sm', sm: 'md' }}
        // Bottom padding für Mobile damit Bottom-Nav nichts überdeckt + safe-area
        style={{ paddingBottom: 'calc(72px + env(safe-area-inset-bottom, 0px))' }}
      >
        <OnboardingBanner user={user} />
        <PushOnboarding />
        <Routes>
          <Route path="/" element={<Swipe />} />
          <Route path="/merkliste" element={<Merkliste />} />
          <Route path="/partner" element={<PartnerCheck />} />
          <Route path="/matches" element={<Matches />} />
          <Route path="/contacted" element={<Contacted />} />
          <Route path="/list" element={<ObjectList />} />
          <Route path="/objects/:id" element={<ObjectDetail />} />
          <Route path="/health" element={<Dashboard />} />
          <Route path="/llm" element={<LLMStatus />} />
          <Route path="/funnel" element={<FunnelTuning />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/search-profiles" element={<SearchProfiles />} />
          <Route path="/account" element={<Account />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/admin/ops" element={<AdminOps />} />
          <Route path="/admin/decisions" element={<AdminDecisions />} />
        </Routes>
      </Container>

      {/* Mobile Bottom-Nav (fixed, immer sichtbar) */}
      <Box
        component="nav"
        hiddenFrom="sm"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          background: '#ffffff',
          borderTop: '1px solid rgba(15,23,42,0.08)',
          zIndex: 50,
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          backdropFilter: 'saturate(180%) blur(8px)',
        }}
      >
        <Group gap={0} wrap="nowrap" justify="space-between">
          {NAV_ITEMS.map((item) => (
            <BottomNavLink
              key={item.to}
              item={item}
              badge={
                item.to === '/partner'
                  ? badges.partner
                  : item.to === '/matches'
                    ? badges.match
                    : undefined
              }
            />
          ))}
        </Group>
      </Box>
    </Box>
  );
}

/**
 * AuthGate: prüft die Session. 401 (AUTH_ENFORCE=true, keine Session) → Login-Maske.
 * Im Übergangsmodus liefert /auth/me immer den Default-User → App lädt direkt.
 */
function AuthGate(): JSX.Element {
  const { data, isLoading, error } = useQuery({
    queryKey: ['auth-me'],
    queryFn: getMe,
    // 401 = nicht angemeldet → nicht retryen (Login zeigen). Netz/5xx → 3× retryen.
    retry: (count, err) => !(err instanceof ApiError && err.status === 401) && count < 3,
    staleTime: 5 * 60_000,
  });

  if (isLoading) {
    return (
      <Center style={{ minHeight: '100vh' }}>
        <Loader />
      </Center>
    );
  }
  // Nur echtes 401 → Login-Maske. Andere Fehler (Server down, Netz) → freundlicher
  // Retry-Screen, NICHT fälschlich ausloggen (Übergangsmodus überlebt Server-Restart).
  if (error) {
    if (error instanceof ApiError && error.status === 401) return <Login />;
    return (
      <Center style={{ minHeight: '100vh', padding: 24 }}>
        <Stack align="center" gap="sm">
          <Text fw={600}>Server nicht erreichbar</Text>
          <Text size="sm" c="dimmed" ta="center">
            Verbindung zu betongold unterbrochen. Neuer Versuch läuft automatisch …
          </Text>
          <Loader size="sm" mt="xs" />
        </Stack>
      </Center>
    );
  }
  if (!data) return <Login />;
  return (
    <AppShell
      user={data.user}
      realUser={data.realUser}
      impersonating={data.impersonating}
      viewAsLogin={data.viewAsLogin}
      googleConfigured={data.googleConfigured}
    />
  );
}

export function App(): JSX.Element {
  return (
    <Router>
      <AuthGate />
    </Router>
  );
}
